import React, { Component, Fragment } from 'react';
import { createRefetchContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';

// components
import { Button } from 'dibs-elements/exports/Button';
import { PdpRespSimilarItemsGrid } from '../PdpRespSimilarItems/PdpRespSimilarItemsGrid';
import { PdpRespComplementaryItemsGrid } from '../PdpRespSimilarItems/PdpRespComplementaryItemsGrid';

// helpers
import { abExperiments } from '../../utils/abExperiments';
import { flattenConnectionNodes } from '../../utils/relayUtils';
import { promiseTimeout } from '../helpers/promiseTimeout';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import {
    SIMILAR_ITEMS_LIST,
    VISUALLY_SIMILAR_ITEMS_LIST,
    trackViewMoreClick,
} from '../tracking/similarItemsTracking';

// messages
import { youMayAlsoLike, visuallySimilar, viewMore } from '../pdpMessages';

import styles from './PdpRespSimilarItemsFooter.scss';

const MAX_VISUALLY_SIMILAR_GRID_ITEMS = 4;

const removeDupeItems = (ids, visuallySimilarGridItems) => {
    return flattenConnectionNodes(visuallySimilarGridItems)
        .filter(item => !ids.includes(item.serviceId))
        .splice(0, MAX_VISUALLY_SIMILAR_GRID_ITEMS);
};

export class PdpRespSimilarItemsFooterComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showComplementaryFirst: false,
        };
    }

    componentDidMount() {
        if (abExperiments.showMoreForProjectFirst) {
            promiseTimeout(1500, abExperiments.showMoreForProjectFirst).then(() => {
                this.setState({ showComplementaryFirst: true });
            });
        }
    }

    componentDidUpdate() {
        const {
            item: { isArt },
            relayVariables: { isClient },
            viewer: { visuallySimilarGridItems },
        } = this.props;
        // TODO: when we will update relay worth revisiting if client check is still needed
        // At the moment If refetch happens and in the meantime relay vars changes, data gets disposed
        // more info: https://github.com/facebook/relay/blob/1.4.1-manual-patch/packages/react-relay/modern/ReactRelayRefetchContainer.js#L114-L118
        if (isClient && isArt && !visuallySimilarGridItems) {
            this.props.relay.refetch({ isArt });
        }
    }

    render() {
        const {
            currency,
            viewer,
            user,
            item,
            similarGridItems,
            relayVariables,
            gridItemWrapper,
            scrollToRef,
        } = this.props;

        const { visuallySimilarGridItems } = viewer || {};
        const { serviceId: itemId } = item;

        const similarItemsIds = flattenConnectionNodes(similarGridItems).map(
            node => node.serviceId
        );

        const sharedItemsGridProps = {
            gridItemWrapper,
            relayVariables: {
                userId: relayVariables.userId,
                isClient: relayVariables.isClient,
            },
            item,
            viewer,
            currency,
            user,
            favoritesLocation: locations.SIMILAR_ITEM_GRID,
        };

        const similarItems = (
            <PdpRespSimilarItemsGrid
                {...sharedItemsGridProps}
                header={youMayAlsoLike}
                scrollToRef={scrollToRef}
                gridLocation={SIMILAR_ITEMS_LIST}
                similarItems={flattenConnectionNodes(similarGridItems)}
            />
        );

        const visuallySimillarItems = visuallySimilarGridItems && (
            <Fragment>
                <PdpRespSimilarItemsGrid
                    {...sharedItemsGridProps}
                    header={visuallySimilar}
                    gridLocation={VISUALLY_SIMILAR_ITEMS_LIST}
                    similarItems={removeDupeItems(similarItemsIds, visuallySimilarGridItems)}
                />
                <div className={`primary-viewport-width ${styles.wrapper}`}>
                    <Button
                        className={styles.button}
                        onClick={() => trackViewMoreClick()}
                        href={`/similar/?item=${itemId}`}
                        size="medium"
                        type="secondary"
                    >
                        {viewMore}
                    </Button>
                </div>
            </Fragment>
        );

        const complementaryItems = (
            <PdpRespComplementaryItemsGrid
                relayVariables={{
                    userId: relayVariables.userId,
                    isClient: relayVariables.isClient,
                }}
                item={item}
                similarItems={similarGridItems}
                viewer={viewer}
                currency={currency}
                user={user}
            />
        );

        if (this.state.showComplementaryFirst) {
            return (
                <Fragment>
                    {complementaryItems}
                    {similarItems}
                    {visuallySimillarItems}
                </Fragment>
            );
        }

        return (
            <Fragment>
                {similarItems}
                {complementaryItems}
                {visuallySimillarItems}
            </Fragment>
        );
    }
}

PdpRespSimilarItemsFooterComponent.propTypes = {
    currency: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    similarGridItems: PropTypes.object,
    gridItemWrapper: PropTypes.string,
    relay: PropTypes.object,
    scrollToRef: PropTypes.object.isRequired,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool.isRequired,
        fetchUser: PropTypes.bool.isRequired,
        relationshipUserIds: PropTypes.array,
        followSearchPages: PropTypes.array,
        fetchFollowSearch: PropTypes.bool,
    }).isRequired,
};

export const PdpRespSimilarItemsFooter = createRefetchContainer(
    PdpRespSimilarItemsFooterComponent,
    {
        viewer: graphql`
            fragment PdpRespSimilarItemsFooter_viewer on Viewer
                @argumentDefinitions(isArt: { type: "Boolean!", defaultValue: false }) {
                visuallySimilarGridItems: visualSearch(itemPk: $itemId, first: 6)
                    @include(if: $isArt) {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                }
                ...PdpRespSimilarItemsGrid_viewer
                ...PdpRespComplementaryItemsGrid_viewer
            }
        `,
        user: graphql`
            fragment PdpRespSimilarItemsFooter_user on User {
                ...PdpRespComplementaryItemsGrid_user
                ...PdpRespSimilarItemsGrid_user
            }
        `,
        item: graphql`
            fragment PdpRespSimilarItemsFooter_item on Item {
                isArt
                serviceId
                ...PdpRespComplementaryItemsGrid_item
            }
        `,
        similarGridItems: graphql`
            fragment PdpRespSimilarItemsFooter_similarGridItems on moreLikeThisConnection {
                edges {
                    node {
                        serviceId
                        ...PdpRespSimilarItemsGrid_similarItems
                    }
                }
            }
        `,
    },
    graphql`
        query PdpRespSimilarItemsFooterRefetchQuery(
            $isArt: Boolean!
            $itemId: String!
            $isTrade: Boolean!
        ) {
            viewer {
                ...PdpRespSimilarItemsFooter_viewer @arguments(isArt: $isArt)
            }
        }
    `
);
